import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Search, SearchX, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/EmptyState";
import {
	SPEAKER_COLORS,
	buildHighlightRegex,
	buildSpeakerMaps,
	filterVttEntries,
	getSpeakerInitials,
	parseVtt,
} from "./utils";
import type { Transcript } from "@/types/transcript";

export interface TranscriptContentProps {
	transcript: Transcript;
}

function highlightMatch(text: string, regex: RegExp | null): React.ReactNode {
	if (!regex)
		return text;
	// split() with a capturing regex places matches at odd indexes.
	return text.split(regex).map((part, index) =>
		index % 2 === 1 ? (
			<mark key={index} className="rounded-sm bg-yellow-200 px-0.5 dark:bg-yellow-800">
				{part}
			</mark>
		) : (
			part
		)
	);
}

/**
 * The transcript body: searchable speaker-bubble view for VTT content, plain
 * preformatted text otherwise.
 */
export const TranscriptContent: React.FC<TranscriptContentProps> = ({ transcript }) => {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");

	const entries = useMemo(() => {
		if (!transcript.transcript_content || transcript.content_type !== "vtt")
			return [];
		return parseVtt(transcript.transcript_content);
	}, [transcript.transcript_content, transcript.content_type]);

	const speakerMaps = useMemo(() => buildSpeakerMaps(entries), [entries]);
	const filteredEntries = useMemo(() => filterVttEntries(entries, search), [entries, search]);
	const highlightRegex = useMemo(() => buildHighlightRegex(search), [search]);

	if (!transcript.transcript_content)
		return (
			<EmptyState
				icon={FileText}
				title={t("transcripts.noContent", "Transcript content is not available.")}
				className="h-full"
			/>
		);

	if (transcript.content_type !== "vtt" || entries.length === 0)
		return (
			<div className="relative h-full min-h-0">
				<div className="absolute inset-0 overflow-y-auto scrollbar-thin p-4">
					<pre className="whitespace-pre-wrap text-sm text-foreground">{transcript.transcript_content}</pre>
				</div>
			</div>
		);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="shrink-0 border-b border-border p-3">
				<div className="relative">
					<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder={t("transcripts.searchTranscript", "Search transcript…")}
						className="h-9 pl-8 pr-8"
						aria-label={t("transcripts.searchTranscript", "Search transcript…")}
					/>
					{search && (
						<button
							type="button"
							onClick={() => setSearch("")}
							className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
							aria-label={t("common.clear", "Clear")}
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
				{search.trim() && (
					<p className="mt-1 text-xs text-muted-foreground">
						{t("transcripts.matchesFound", "{{count}} results", { count: filteredEntries.length })}
					</p>
				)}
			</div>
			<div className="relative min-h-0 flex-1">
				<div className="absolute inset-0 overflow-y-auto scrollbar-thin p-4">
					{filteredEntries.length === 0 ? (
						<EmptyState
							icon={SearchX}
							title={t("transcripts.noSearchResults", "No matches found.")}
							className="h-full"
						/>
					) : (
						<div className="space-y-2">
							{filteredEntries.map((entry, index) => {
								const colors = speakerMaps.colors.get(entry.speaker) || SPEAKER_COLORS[0];
								const side = speakerMaps.sides.get(entry.speaker) || "left";
								const previous = index > 0 ? filteredEntries[index - 1] : null;
								const isNewSpeaker = !previous || entry.speaker !== previous.speaker;
								const isRight = side === "right";
								return (
									<div
										key={`${entry.timestamp}-${index}`}
										className={cn("flex gap-3", isNewSpeaker && index > 0 && "mt-3", isRight && "flex-row-reverse")}
									>
										<div className="w-8 shrink-0">
											{isNewSpeaker && (
												<div
													className={cn(
														"flex h-8 w-8 items-center justify-center rounded-sm border",
														colors.bg,
														colors.border
													)}
												>
													<span className={cn("text-xs font-semibold", colors.name)}>
														{getSpeakerInitials(entry.speaker || "?")}
													</span>
												</div>
											)}
										</div>
										<div className={cn("min-w-0 flex-1", isRight && "flex flex-col items-end")}>
											{isNewSpeaker && (
												<div className={cn("mb-1 flex items-baseline gap-2", isRight && "flex-row-reverse")}>
													<span className={cn("text-xs font-semibold", colors.name)}>
														{entry.speaker || t("transcripts.unknownSpeaker", "Unknown")}
													</span>
													<span className="text-[10px] tabular-nums text-muted-foreground">{entry.timestamp}</span>
												</div>
											)}
											<div className={cn("inline-flex max-w-[85%] rounded-sm border px-3 py-1.5", colors.bg, colors.border)}>
												<p className="text-sm text-foreground">{highlightMatch(entry.text, highlightRegex)}</p>
												{!isNewSpeaker && (
													<span className="ml-2 shrink-0 self-end text-[10px] tabular-nums text-muted-foreground">
														{entry.timestamp}
													</span>
												)}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
